// Excel / CSV import with column mapping: any spreadsheet of customers and vendors, accounts, items, opening balances,
// journals, or sales invoices and purchase bills becomes the canonical JSON (sql/008) and goes through the same loader
// as a Tally or Zoho import: checked first, all or nothing, re-runnable (rows already in are skipped).
// Pure: no network or DOM, so it is tested on its own (sheetImport.test.ts).
import { bankDate, excelDate } from "../banking/bankCsv";

export type SheetKind = "parties" | "accounts" | "items" | "openings" | "journals" | "sales" | "purchases";
export interface Field { key: string; label: string; required?: boolean; aliases: string[]; hint?: string }
export interface Template { title: string; about: string; fields: Field[] }

const F = (key: string, label: string, aliases: string[], required = false, hint?: string): Field => ({ key, label, aliases, required, hint });

export const TEMPLATES: Record<SheetKind, Template> = {
  parties: { title: "Customers and vendors", about: "One row per customer or vendor. A name already in the books is left as it is.", fields: [
    F("name", "Name", ["name", "party name", "customer name", "vendor name", "supplier name", "display name", "company name", "ledger name"], true),
    F("type", "Customer or vendor", ["type", "party type", "customer or vendor", "kind", "category"], false, "customer, vendor or both; blank uses the choice above"),
    F("gstin", "GSTIN", ["gstin", "gst number", "gst no", "gstin/uin", "gst in", "gst registration"]),
    F("state", "State", ["state", "place of supply", "state name", "billing state"]),
    F("pan", "PAN", ["pan", "pan number", "pan no", "it pan"]),
    F("email", "Email", ["email", "email id", "email address", "e-mail"]),
    F("phone", "Phone", ["phone", "mobile", "mobile number", "phone number", "contact number", "tel"]),
    F("contact", "Contact person", ["contact person", "contact name", "contact"]),
    F("address", "Address", ["address", "billing address", "address line 1", "street"]),
    F("city", "City", ["city", "town", "place", "billing city"]),
    F("pincode", "PIN code", ["pincode", "pin code", "pin", "zip", "postal code", "billing code"]),
    F("credit_days", "Credit days", ["credit days", "credit period", "payment terms", "terms (days)", "due days"]),
    F("opening", "Opening balance", ["opening balance", "opening", "balance", "opening bal", "outstanding"]),
    F("dr_cr", "Dr / Cr", ["dr/cr", "dr cr", "side", "balance type", "drcr"], false, "Dr or Cr; blank: Dr for customers, Cr for vendors; a minus sign means the other side"),
  ] },
  accounts: { title: "Chart of accounts", about: "One row per ledger with its group (a Books group, or a common name such as Sundry Debtors, Indirect Expenses).", fields: [
    F("name", "Ledger name", ["name", "ledger", "ledger name", "account", "account name"], true),
    F("group", "Group", ["group", "under", "parent", "account group", "account type", "type"], true),
    F("opening", "Opening balance", ["opening balance", "opening", "balance"]),
    F("dr_cr", "Dr / Cr", ["dr/cr", "dr cr", "side", "drcr"]),
    F("hsn", "HSN / SAC", ["hsn", "sac", "hsn/sac", "hsn code"]),
    F("gst_rate", "GST rate %", ["gst rate", "gst %", "tax rate", "rate", "gst"]),
  ] },
  items: { title: "Items and opening stock", about: "One row per item (or per batch of an item's opening stock). Units, categories and warehouses not in the books are made.", fields: [
    F("name", "Item name", ["name", "item", "item name", "product", "product name", "stock item"], true),
    F("code", "Code / SKU", ["code", "sku", "item code", "product code", "part number"]),
    F("unit", "Unit", ["unit", "uom", "units", "unit of measure"]),
    F("category", "Category", ["category", "item group", "stock group", "group"]),
    F("hsn", "HSN", ["hsn", "hsn code", "hsn/sac", "sac"]),
    F("gst_rate", "GST rate %", ["gst rate", "gst %", "tax rate", "rate %", "gst"]),
    F("mrp", "MRP", ["mrp", "max retail price"]),
    F("description", "Description", ["description", "details"]),
    F("qty", "Opening quantity", ["opening qty", "opening quantity", "quantity", "qty", "stock", "on hand", "closing qty"]),
    F("value", "Opening value", ["opening value", "value", "stock value", "amount", "closing value"]),
    F("location", "Warehouse", ["warehouse", "location", "godown", "store"]),
    F("batch", "Batch", ["batch", "batch no", "lot"]),
    F("expiry", "Expiry", ["expiry", "expiry date", "exp date", "best before"]),
  ] },
  openings: { title: "Opening balances", about: "A trial balance at the date the books begin: each ledger with its debit or credit. Ledgers must already be in the books (import the chart of accounts first).", fields: [
    F("ledger", "Ledger", ["ledger", "ledger name", "account", "account name", "particulars", "name"], true),
    F("debit", "Debit", ["debit", "dr", "debit amount", "dr amount"]),
    F("credit", "Credit", ["credit", "cr", "credit amount", "cr amount"]),
    F("amount", "Amount (Dr +, Cr −)", ["amount", "balance", "closing balance", "opening balance"]),
    F("dr_cr", "Dr / Cr", ["dr/cr", "side", "drcr"]),
  ] },
  journals: { title: "Journals and other entries", about: "One row per line; rows with the same entry number (and type) make one entry, and each entry must balance.", fields: [
    F("number", "Entry number", ["voucher no", "voucher number", "entry no", "entry number", "journal no", "number", "no", "ref"], true),
    F("date", "Date", ["date", "voucher date", "entry date", "journal date"], true),
    F("type", "Type", ["voucher type", "type", "entry type"], false, "Journal, Payment, Receipt, Contra… blank is Journal"),
    F("ledger", "Ledger", ["ledger", "account", "account name", "ledger name", "particulars"], true),
    F("debit", "Debit", ["debit", "dr", "debit amount"]),
    F("credit", "Credit", ["credit", "cr", "credit amount"]),
    F("narration", "Narration", ["narration", "description", "notes", "memo", "remarks"]),
  ] },
  sales: { title: "Sales invoices", about: "One row per invoice line; rows with the same invoice number make one invoice. GST is worked out at the rate, CGST + SGST within the state, IGST outside it.", fields: [
    F("number", "Invoice number", ["invoice number", "invoice no", "invoice #", "bill no", "number", "voucher no"], true),
    F("date", "Invoice date", ["invoice date", "date", "bill date"], true),
    F("party", "Customer", ["customer", "customer name", "party", "party name", "name", "billed to"], true),
    F("account", "Sales account", ["account", "sales account", "income account", "ledger"], false, "blank: Sales"),
    F("description", "Description", ["description", "item", "item name", "particulars", "product"]),
    F("hsn", "HSN / SAC", ["hsn", "sac", "hsn/sac", "hsn code"]),
    F("qty", "Quantity", ["quantity", "qty"]),
    F("uqc", "Unit", ["unit", "uqc", "uom"]),
    F("taxable", "Taxable value", ["taxable value", "taxable amount", "amount", "item total", "sub total", "subtotal", "value"], true),
    F("gst_rate", "GST rate %", ["gst rate", "gst %", "tax rate", "rate %", "tax %", "gst"]),
    F("pos", "Place of supply", ["place of supply", "pos", "state"]),
    F("party_gstin", "Customer GSTIN", ["gstin", "customer gstin", "party gstin", "gstin/uin"]),
    F("due_date", "Due date", ["due date", "due on"]),
  ] },
  purchases: { title: "Purchase bills", about: "One row per bill line; rows with the same bill number and vendor make one bill. Input GST is worked out at the rate.", fields: [
    F("number", "Bill number", ["bill number", "bill no", "invoice number", "invoice no", "supplier invoice no", "vendor invoice no", "number"], true),
    F("date", "Bill date", ["bill date", "invoice date", "date"], true),
    F("party", "Vendor", ["vendor", "vendor name", "supplier", "supplier name", "party", "party name", "name"], true),
    F("account", "Purchase account", ["account", "purchase account", "expense account", "ledger"], false, "blank: Purchases"),
    F("description", "Description", ["description", "item", "item name", "particulars", "product"]),
    F("hsn", "HSN / SAC", ["hsn", "sac", "hsn/sac", "hsn code"]),
    F("qty", "Quantity", ["quantity", "qty"]),
    F("uqc", "Unit", ["unit", "uqc", "uom"]),
    F("taxable", "Taxable value", ["taxable value", "taxable amount", "amount", "item total", "sub total", "subtotal", "value"], true),
    F("gst_rate", "GST rate %", ["gst rate", "gst %", "tax rate", "rate %", "tax %", "gst"]),
    F("pos", "Vendor's state", ["vendor state", "supplier state", "place of supply", "pos", "state"], false, "used when the vendor has no GSTIN"),
    F("party_gstin", "Vendor GSTIN", ["gstin", "vendor gstin", "supplier gstin", "party gstin", "gstin/uin"]),
    F("due_date", "Due date", ["due date", "due on"]),
  ] },
};

// ---------- reading cells ----------
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9%#/]+/g, " ").trim();

export function num(s: string | undefined): number | null {
  if (s == null) return null;
  let t = String(s).trim();
  if (!t) return null;
  let sign = 1;
  if (/^\(.*\)$/.test(t)) { sign = -1; t = t.slice(1, -1); }
  if (/\bcr\.?$/i.test(t)) { sign = -sign; t = t.replace(/\s*cr\.?$/i, ""); } else t = t.replace(/\s*dr\.?$/i, "");
  t = t.replace(/[₹,\s]|rs\.?|inr/gi, "");
  if (t.endsWith("-")) { sign = -sign; t = t.slice(0, -1); }
  if (!/^[-+]?\d*\.?\d+$/.test(t)) return NaN;
  return sign * Number(t);
}

export function date(s: string | undefined): string | null {
  const t = (s ?? "").trim();
  if (!t) return null;
  if (/^\d{5}(\.\d+)?$/.test(t)) return excelDate(Number(t));      // a date Excel kept as a serial number
  return bankDate(t) ?? null;
}

const side = (s: string | undefined): "Dr" | "Cr" | null => {
  const t = (s ?? "").trim().toLowerCase();
  return /^(dr|debit|d)\.?$/.test(t) ? "Dr" : /^(cr|credit|c)\.?$/.test(t) ? "Cr" : null;
};

// ---------- the header row and the mapping ----------
// the first row (of the first ten) that names at least one required field; columns matched by name
export function findHeader(table: string[][], fields: Field[]): number {
  let best = 0, bestScore = -1;
  for (let i = 0; i < Math.min(table.length, 10); i++) {
    const m = guessMapping(table[i], fields);
    const score = fields.filter((f) => m[f.key] >= 0).length + fields.filter((f) => f.required && m[f.key] >= 0).length * 2;
    if (score > bestScore) { best = i; bestScore = score; }
  }
  return best;
}

export function guessMapping(header: string[], fields: Field[]): Record<string, number> {
  const h = header.map((x) => norm(x ?? ""));
  const used = new Set<number>(); const out: Record<string, number> = {};
  // exact names first (in field order), then a header that starts with an alias
  for (const pass of [0, 1]) {
    for (const f of fields) {
      if (out[f.key] >= 0) continue;
      const names = [norm(f.label), ...f.aliases.map(norm)];
      const i = h.findIndex((x, j) => !used.has(j) && x && (pass === 0 ? names.includes(x) : names.some((n) => n.length > 3 && x.startsWith(n))));
      if (i >= 0) { out[f.key] = i; used.add(i); }
    }
  }
  for (const f of fields) if (!(out[f.key] >= 0)) out[f.key] = -1;
  return out;
}

export interface SheetRow { row: number; v: Record<string, string> }
export function rowsOf(table: string[][], headerRow: number, mapping: Record<string, number>): SheetRow[] {
  const out: SheetRow[] = [];
  for (let i = headerRow + 1; i < table.length; i++) {
    const r = table[i];
    if (!r || r.every((c) => !String(c ?? "").trim())) continue;
    const v: Record<string, string> = {};
    for (const [k, col] of Object.entries(mapping)) v[k] = col >= 0 ? String(r[col] ?? "").trim() : "";
    out.push({ row: i + 1, v });
  }
  return out;
}

// ---------- to the canonical JSON ----------
export interface TaxLedger { ledger: string; head: string; direction: string; rcm: boolean }
export interface Ctx {
  source?: string;
  defaultPartyType?: "customer" | "supplier" | "both";
  baseState: string | null;                         // the company's (default) GST state code
  states: { code: string; name: string }[];
  taxLedgers: TaxLedger[];
  parties: { name: string; state: string | null }[]; // parties already in the books, with their GST state
  booksBegin: string;
}
export interface Problem { row: number; message: string; note?: boolean }   // a note is said but doesn't stop the import
export interface Converted { data: Record<string, unknown>; problems: Problem[]; openings?: { ledger: string; side: "Dr" | "Cr"; amount: number }[]; summary: string }

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const GSTIN = /^[0-9]{2}[A-Z0-9]{10}[0-9A-Z]Z[0-9A-Z]$/;
const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const r2 = (n: number) => Math.round(n * 100) / 100;

export function stateCode(s: string | undefined, ctx: Ctx): string | null {
  const t = (s ?? "").trim();
  if (!t) return null;
  if (/^\d{1,2}$/.test(t)) return t.padStart(2, "0");
  const m = /^(\d{2})\s*[-–:]/.exec(t); if (m) return m[1];     // "27-Maharashtra"
  const n = norm(t);
  return ctx.states.find((x) => norm(x.name) === n)?.code ?? null;
}

function partyType(s: string | undefined, ctx: Ctx): "customer" | "supplier" | "both" | null {
  const t = (s ?? "").trim().toLowerCase();
  if (!t) return ctx.defaultPartyType ?? null;
  if (/both/.test(t)) return "both";
  if (/(cust|debtor|client|buyer)/.test(t)) return "customer";
  if (/(vend|suppl|creditor|seller)/.test(t)) return "supplier";
  return null;
}

export function toCanonical(kind: SheetKind, rows: SheetRow[], ctx: Ctx): Converted {
  const problems: Problem[] = [];
  const bad = (row: number, message: string) => { problems.push({ row, message }); };
  const source = ctx.source ?? "excel";
  const data: Record<string, unknown> = { source, source_company: "Spreadsheet" };

  if (kind === "parties") {
    const ledgers: Record<string, unknown>[] = []; const seen = new Set<string>();
    for (const { row, v } of rows) {
      if (!v.name) { bad(row, "no name"); continue; }
      if (seen.has(v.name.toLowerCase())) { bad(row, `"${v.name}" is on two rows`); continue; }
      seen.add(v.name.toLowerCase());
      const t = partyType(v.type, ctx);
      if (!t) { bad(row, `say whether "${v.name}" is a customer or a vendor`); continue; }
      const gstin = v.gstin.toUpperCase().replace(/\s/g, "");
      if (gstin && !GSTIN.test(gstin)) bad(row, `GSTIN ${v.gstin} doesn't look right`);
      const pan = (v.pan || (gstin.length === 15 ? gstin.slice(2, 12) : "")).toUpperCase();
      if (v.pan && !PAN.test(pan)) bad(row, `PAN ${v.pan} doesn't look right`);
      if (v.email && !EMAIL.test(v.email)) bad(row, `email ${v.email} doesn't look right`);
      const phone = v.phone.replace(/[^\d+ ()-]/g, "");
      const st = stateCode(v.state, ctx);
      if (v.state && !st) bad(row, `state "${v.state}" not recognised`);
      const days = num(v.credit_days);
      if (days != null && (Number.isNaN(days) || days < 0)) bad(row, `credit days "${v.credit_days}" isn't a number`);
      const o = num(v.opening);
      if (o != null && Number.isNaN(o)) bad(row, `opening balance "${v.opening}" isn't a number`);
      const given = side(v.dr_cr);
      const l: Record<string, unknown> = { key: v.name, name: v.name, group: t === "supplier" ? "trade_payables" : "trade_receivables", party: t,
        billwise: { enabled: true, credit_days: days && !Number.isNaN(days) ? Math.round(days) : 0 },
        gst: { party: { gstin: gstin || null, state: st, address: v.address || null, place: v.city || null, pincode: v.pincode || null } },
        tds: { pan: PAN.test(pan) ? pan : null } };
      if (v.email || phone || v.contact) l.contact = { email: v.email || null, phone: phone.length >= 6 ? phone : null, contact_person: v.contact || null };
      if (o && !Number.isNaN(o)) {
        const s0 = given ?? (t === "supplier" ? "Cr" : "Dr");
        const s1 = o < 0 ? (s0 === "Dr" ? "Cr" : "Dr") : s0;
        l.opening = { side: s1, amount: r2(Math.abs(o)) };
        // the opening as one bill on account, so bill-wise ageing starts right
        l.bills = [{ ref: "Opening", kind: "new", date: ctx.booksBegin, side: s1, amount: r2(Math.abs(o)) }];
      }
      ledgers.push(l);
    }
    data.ledgers = ledgers;
    return { data, problems, summary: `${ledgers.length} customers and vendors` };
  }

  if (kind === "accounts") {
    const ledgers: Record<string, unknown>[] = [];
    for (const { row, v } of rows) {
      if (!v.name) { bad(row, "no ledger name"); continue; }
      if (!v.group) { bad(row, `"${v.name}": no group`); continue; }
      const o = num(v.opening);
      if (o != null && Number.isNaN(o)) bad(row, `opening balance "${v.opening}" isn't a number`);
      const l: Record<string, unknown> = { key: v.name, name: v.name, group: v.group };
      if (o && !Number.isNaN(o)) {
        const s0 = side(v.dr_cr) ?? "Dr";
        l.opening = { side: o < 0 ? (s0 === "Dr" ? "Cr" : "Dr") : s0, amount: r2(Math.abs(o)) };
      }
      const rate = num(v.gst_rate);
      if (v.hsn || (rate != null && !Number.isNaN(rate))) l.gst = { item: { hsn: v.hsn || null, rate: rate != null && !Number.isNaN(rate) ? rate : null } };
      ledgers.push(l);
    }
    data.ledgers = ledgers;
    return { data, problems, summary: `${ledgers.length} ledgers` };
  }

  if (kind === "items") {
    const items = new Map<string, Record<string, unknown>>(); const units = new Set<string>(); const cats = new Set<string>(); const locs = new Set<string>();
    const lines: Record<string, unknown>[] = [];
    for (const { row, v } of rows) {
      if (!v.name) { bad(row, "no item name"); continue; }
      const k = (v.code || v.name).toLowerCase();
      if (!items.has(k)) {
        const rate = num(v.gst_rate); const mrp = num(v.mrp);
        if (v.hsn && !/^[0-9]{4,8}$/.test(v.hsn)) bad(row, `HSN ${v.hsn} should be 4 to 8 digits`);
        items.set(k, { key: v.code || v.name, name: v.name, code: v.code || null, sku: v.code || null, unit: v.unit || null, category: v.category || null,
          mrp: mrp && !Number.isNaN(mrp) ? mrp : null, description: v.description || null,
          gst: v.hsn ? { hsn: v.hsn, rate: rate != null && !Number.isNaN(rate) ? rate : null } : null });
        if (v.unit) units.add(v.unit); if (v.category) cats.add(v.category);
      }
      const q = num(v.qty), val = num(v.value);
      if (q != null && Number.isNaN(q)) bad(row, `quantity "${v.qty}" isn't a number`);
      if (val != null && Number.isNaN(val)) bad(row, `value "${v.value}" isn't a number`);
      if (q && !Number.isNaN(q)) {
        if (q < 0) bad(row, `negative opening stock for "${v.name}"`);
        if (val == null) bad(row, `"${v.name}": give the opening value with the quantity`);
        const exp = date(v.expiry);
        if (v.expiry && !exp) bad(row, `expiry "${v.expiry}" isn't a date`);
        if (v.location) locs.add(v.location);
        lines.push({ item: v.code || v.name, sku: v.code || null, qty: q, value: val && !Number.isNaN(val) ? r2(val) : 0, location: v.location || null,
                     batch: v.batch || null, expiry: exp, unit: v.unit || null });
      }
    }
    data.units = [...units].map((u) => ({ key: u, code: u, name: u }));
    data.item_categories = [...cats].map((c) => ({ key: c, name: c }));
    data.locations = [...locs].map((l) => ({ key: l, name: l, kind: "warehouse" }));
    data.items = [...items.values()];
    if (lines.length) data.opening_stock = { as_on: ctx.booksBegin, lines };
    return { data, problems, summary: `${items.size} items${lines.length ? `, ${lines.length} opening stock lines` : ""}` };
  }

  if (kind === "openings") {
    const openings: { ledger: string; side: "Dr" | "Cr"; amount: number }[] = [];
    let dr = 0, cr = 0;
    for (const { row, v } of rows) {
      if (!v.ledger) continue;
      if (/^(total|grand total|difference)/i.test(v.ledger)) continue;
      const d = num(v.debit) ?? 0, c = num(v.credit) ?? 0, a = num(v.amount);
      if (Number.isNaN(d) || Number.isNaN(c) || (a != null && Number.isNaN(a))) { bad(row, `"${v.ledger}": the amount isn't a number`); continue; }
      let net = d - c;
      if (a != null && !d && !c) net = side(v.dr_cr) === "Cr" ? -Math.abs(a) : side(v.dr_cr) === "Dr" ? Math.abs(a) : a;
      if (!net) continue;
      openings.push({ ledger: v.ledger, side: net > 0 ? "Dr" : "Cr", amount: r2(Math.abs(net)) });
      if (net > 0) dr += net; else cr -= net;
    }
    if (Math.abs(dr - cr) >= 0.005) problems.push({ row: 0, note: true, message: `debits (${r2(dr)}) and credits (${r2(cr)}) differ by ${r2(dr - cr)}; Books shows any difference as an opening difference until it is cleared` });
    return { data, problems, openings, summary: `${openings.length} opening balances` };
  }

  if (kind === "journals") {
    const groups = new Map<string, SheetRow[]>();
    for (const r of rows) {
      const k = `${(r.v.type || "Journal").toLowerCase()}|${r.v.number}`;
      if (!r.v.number) { bad(r.row, "no entry number"); continue; }
      groups.set(k, [...(groups.get(k) ?? []), r]);
    }
    const vouchers: Record<string, unknown>[] = [];
    for (const [k, rs] of groups) {
      const first = rs[0]; const d = date(first.v.date);
      if (!d) { bad(first.row, `entry ${first.v.number}: date "${first.v.date}" isn't a date`); continue; }
      const lines: Record<string, unknown>[] = []; let net = 0; let ok = true;
      for (const { row, v } of rs) {
        if (!v.ledger) { bad(row, "no ledger"); ok = false; continue; }
        const dr = num(v.debit) ?? 0, cr = num(v.credit) ?? 0;
        if (Number.isNaN(dr) || Number.isNaN(cr)) { bad(row, "the debit or credit isn't a number"); ok = false; continue; }
        const amt = r2(dr - cr);
        if (!amt) continue;
        lines.push({ ledger: v.ledger, side: amt > 0 ? "Dr" : "Cr", amount: Math.abs(amt), narration: null });
        net += amt;
      }
      if (Math.abs(net) >= 0.005) { bad(first.row, `entry ${first.v.number} doesn't balance (off by ${r2(net)})`); ok = false; }
      if (lines.length < 2) { bad(first.row, `entry ${first.v.number} needs at least two lines`); ok = false; }
      if (ok) vouchers.push({ key: k, type: first.v.type || "Journal", date: d, number: first.v.number,
                              narration: rs.map((r) => r.v.narration).find(Boolean) || null, lines });
    }
    data.vouchers = vouchers;
    return { data, problems, summary: `${vouchers.length} entries` };
  }

  // sales invoices and purchase bills
  const sales = kind === "sales";
  const dir = sales ? "output" : "input";
  const taxLedger = (head: string) => ctx.taxLedgers.find((t) => t.head === head && t.direction === dir && !t.rcm)?.ledger ?? null;
  const groups = new Map<string, SheetRow[]>();
  for (const r of rows) {
    if (!r.v.number) { bad(r.row, `no ${sales ? "invoice" : "bill"} number`); continue; }
    if (!r.v.party) { bad(r.row, `no ${sales ? "customer" : "vendor"}`); continue; }
    const k = sales ? r.v.number.toLowerCase() : `${r.v.party.toLowerCase()}|${r.v.number.toLowerCase()}`;
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  const vouchers: Record<string, unknown>[] = []; const missingTax = new Set<string>();
  // a company with no GSTIN in Books: tax can't be worked out; with no rates the documents come in without GST
  const noGst = !ctx.baseState;
  if (noGst && rows.some((r) => (num(r.v.gst_rate) ?? 0) > 0)) {
    problems.push({ row: 0, message: `this company has no GSTIN in Books, so the GST on these ${sales ? "invoices" : "bills"} can't be worked out. Add the GSTIN under Settings › GST (and its tax ledgers), or clear the GST rate column to bring them in without tax` });
    return { data, problems, summary: `0 ${sales ? "invoices" : "bills"}` };
  }
  for (const [k, rs] of groups) {
    const first = rs[0]; const v0 = first.v; const d = date(v0.date);
    if (!d) { bad(first.row, `${v0.number}: date "${v0.date}" isn't a date`); continue; }
    const due = date(v0.due_date);
    const known = ctx.parties.find((p) => p.name.toLowerCase() === v0.party.toLowerCase());
    // sales: where the supply goes; purchases: the vendor's state (Books compares it with the company's own)
    const fromGstin = /^\d{2}/.test(v0.party_gstin) ? v0.party_gstin.slice(0, 2) : null;
    const pos = sales ? stateCode(v0.pos, ctx) ?? fromGstin ?? known?.state ?? ctx.baseState
                      : fromGstin ?? known?.state ?? stateCode(v0.pos, ctx) ?? ctx.baseState;
    if (v0.pos && !stateCode(v0.pos, ctx)) bad(first.row, `${v0.number}: place of supply "${v0.pos}" not recognised`);
    const intra = pos != null && pos === ctx.baseState;
    const lines: Record<string, unknown>[] = []; const tax: Record<string, number> = {}; let total = 0; let ok = true;
    for (const { row, v } of rs) {
      const t = num(v.taxable); const rate = num(v.gst_rate) ?? 0; const q = num(v.qty);
      if (t == null || Number.isNaN(t)) { bad(row, `${v0.number}: taxable value "${v.taxable}" isn't a number`); ok = false; continue; }
      if (Number.isNaN(rate) || ![0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28, 40].includes(rate)) { bad(row, `${v0.number}: GST rate "${v.gst_rate}" isn't a GST rate`); ok = false; continue; }
      const amt = r2(t);
      lines.push({ ledger: v.account || (sales ? "Sales" : "Purchases"), side: sales ? "Cr" : "Dr", amount: amt,
        ...(noGst ? { narration: v.description || null } :
          { gst: { hsn: v.hsn || null, rate, description: v.description || null, quantity: q && !Number.isNaN(q) ? q : null, uqc: v.uqc ? v.uqc.toUpperCase().slice(0, 3) : null } }) });
      total += amt;
      if (rate) {
        if (intra) { const half = r2(amt * rate / 200); tax.cgst = r2((tax.cgst ?? 0) + half); tax.sgst = r2((tax.sgst ?? 0) + half); }
        else tax.igst = r2((tax.igst ?? 0) + r2(amt * rate / 100));
      }
    }
    for (const [head, a] of Object.entries(tax)) {
      if (!a) continue;
      const l = taxLedger(head);
      if (!l) { missingTax.add(head); ok = false; continue; }
      lines.push({ ledger: l, side: sales ? "Cr" : "Dr", amount: a });
      total += a;
    }
    total = r2(total);
    if (!ok) continue;
    lines.unshift({ ledger: v0.party, side: sales ? "Dr" : "Cr", amount: total,
      bills: [{ kind: "new", ref: v0.number, bill_date: d, due_date: due, amount: total }] });
    vouchers.push({ key: `${kind}|${k}`, type: sales ? "sales" : "purchase", date: d, number: sales ? v0.number : null,
      reference_no: sales ? null : v0.number, reference_date: sales ? null : d, party: v0.party, lines,
      ...(noGst ? {} : { gst: { place_of_supply: pos, ...(sales ? {} : { supplier_doc_no: v0.number, supplier_doc_date: d }) } }) });
  }
  // the sales or purchase accounts named (or the default): linked when the books have them, else made
  const accounts = new Set<string>();
  for (const v of vouchers) for (const l of (v.lines as Record<string, unknown>[]).slice(1)) if (l.gst || (noGst && l.ledger)) accounts.add(String(l.ledger));
  data.ledgers = [...accounts].map((name) => ({ key: name, name, group: sales ? "revenue_from_operations" : "purchases" }));
  for (const h of missingTax) problems.push({ row: 0, message: `no ${dir} ${h.toUpperCase()} ledger is set up for the GSTIN; make the tax ledgers under Settings › GST first` });
  data.vouchers = vouchers;
  return { data, problems, summary: `${vouchers.length} ${sales ? "invoices" : "bills"}` };
}

// a blank template to download: the field labels as the header row
export const templateHeader = (kind: SheetKind) => TEMPLATES[kind].fields.map((f) => f.label);
